import {
  LandingPageKind,
  LandingSignupFlowActionType,
  type LandingPageConfig,
  type LandingSectionConfig,
} from "../entities/LandingPage";

/**
 * A page of the offer that used to exist only in code, shipped as a builder template.
 * The admin creates a `landing_page` row from it (`slug`), which a funnel step can then
 * use, copy and edit. The hand-made `/oferta/*` routes keep rendering from code.
 */
export interface LandingTemplate {
  /** Stable id, also the last part of `slug`. */
  key: string;
  /** Polish name in the admin. */
  name: string;
  kind: LandingPageKind;
  /** Route the template was copied from, to compare against. */
  sourceRoute: string;
  config: LandingPageConfig;
}

/** Builder slugs of funnel templates; never reachable under `/metamorfoza/*`. */
export const FUNNEL_TEMPLATE_SLUG_PREFIX = "szablony/";

export const getLandingTemplateSlug = (key: string) => `${FUNNEL_TEMPLATE_SLUG_PREFIX}${key}`;

interface FunnelPageConfigInput {
  title: string;
  description?: string;
  ogImage?: string;
  /** PostHog page name of the section tracking. */
  pageName: string;
  /**
   * Frame of the coded page: `embedding` = `EmbeddingLayout` (checkout, post-purchase,
   * thank-you), `landing` = `LandingLayout`, `none` = no layout (the Bikini landings).
   */
  shell: "landing" | "embedding" | "none";
  /** Radix accent of the page, e.g. `ruby`, `gold`, `crimson`, `pink`. */
  accentColor?: string;
  /** Class of the wrapper around all sections. */
  backgroundClassName?: string;
  sections: LandingSectionConfig[];
}

/**
 * A full builder config for a funnel page. Funnel pages have no signup modal, but the
 * schema requires a `signupFlow`, so they get an inert one.
 */
export const createFunnelPageConfig = ({
  title,
  description,
  ogImage,
  pageName,
  shell,
  accentColor,
  backgroundClassName,
  sections,
}: FunnelPageConfigInput): LandingPageConfig => ({
  seo: { title, description: description ?? title, ...(ogImage ? { ogImage } : {}) },
  tracking: { pageName },
  layout: {
    forcedTheme: "light",
    visualTheme: "pink",
    fontPreset: "hollywood",
    shell,
    ...(accentColor ? { accentColor } : {}),
    backgroundClassName: backgroundClassName ?? "bg-white",
  },
  signupFlow: {
    eventTypeUri: "-",
    questions: [],
    storageKeyPrefix: `funnel-${pageName}`,
    qualifiedAction: { type: LandingSignupFlowActionType.GoToPackagesPage },
    packageSelectAction: { type: LandingSignupFlowActionType.GoToPackagesPage },
    bookingCompleteAction: { type: LandingSignupFlowActionType.GoToBeforeConsultation },
  },
  sections,
});

/** Shorthand for a template section. */
export const templateSection = (
  id: string,
  type: LandingSectionConfig["type"],
  props: Record<string, unknown> = {},
  options: { enabled?: boolean; trackingName?: string } = {},
): LandingSectionConfig => ({
  id,
  type,
  enabled: options.enabled ?? true,
  ...(options.trackingName ? { trackingName: options.trackingName } : {}),
  props,
});

export { LandingPageKind };
